import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SaveIcon from "@mui/icons-material/Save";
import { toast } from "react-toastify";
import { BASE_URL } from "../../../global";
import axiosWithToken from "../../../utils/commonComponent/axiosTokenConfig";
import moment from "moment";
import { fetchQuery } from "../../../utils/commonComponent/fetched";
import { AutoCompletes, DateInput, TextFields } from "../../../utils/Custom/custominput";
import { format } from "date-fns";
import { FileUploadComponent } from "../inspectionReport/IRMultipleFileUpload";

const MODES_OF_TRAVEL = [
  "Car",
  "Train",
  "Bus",
  "Flight",
  "Two Wheeler",
  "Other",
];

const EMPTY_FORM = {
  IRId: "",
  clientProject: "",
  clientPo: "",
  visitDate: "",
  vendorDetails: "",
  travelFrom: "",
  travelTo: "",
  modeOfTravel: "",
  distanceKm: "",
  inTime: "",
  outTime: "",
  totalHours: "",
  expenditureInr: "",
  remarks: "",
  supportingDocuments: "",
  period: "",
  status: 1,
};

const TimeSheetModal = ({ open, onClose, rowData }) => {
  const userID = localStorage.getItem("id") || null;

  const [existingId, setExistingId] = useState(null); // null = POST, number = PATCH
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  // ─── On open: prefill from rowData, then check for existing record ────────
  useEffect(() => {
    if (open && rowData) {
      const prefilled = buildPrefill(rowData);
      setForm(prefilled);
      setExistingId(null);

      // Only try to fetch existing record if reportNo AND visitDate are present
      const hasReportNo = !!rowData?.reportNo?.trim();
      const hasVisitDate = !!rowData?.visitDate;

      if (hasReportNo && hasVisitDate) {
        fetchExistingTimeSheet(prefilled);
      }
      // else: no reportNo or no visitDate → always POST, keep prefill
    }
  }, [open, rowData]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM);
      setExistingId(null);
    }
  }, [open]);

  const buildPrefill = (row) => ({
    ...EMPTY_FORM,
    IRId: row?.id || "",
    clientProject: row?.projectName || "",
    clientPo: row?.purchaseOrder || "",
    visitDate: row?.visitDate ? moment(row.visitDate).format("YYYY-MM-DD") : "",
    vendorDetails: row?.supplierName || "",
    createBy: userID,
  });

  const fetchExistingTimeSheet = async (prefilled) => {
    setLoading(true);
    try {
      let query = `select * from timeSheet where IRId = ${rowData.id}`;
      const res = await fetchQuery(query);
      const record = res?.[0];
      if (record) {
        // Record found → PATCH
        setExistingId(record.id);
        setForm({
          IRId: record.IRId ?? prefilled.IRId,
          clientProject: record.clientProject,
          clientPo: record.clientPo,
          visitDate: record.visitDate
            ? moment(record.visitDate).format("YYYY-MM-DD")
            : prefilled.visitDate,
          vendorDetails: record.vendorDetails,
          travelFrom: record.travelFrom || "",
          travelTo: record.travelTo || "",
          modeOfTravel: record.modeOfTravel || "",
          distanceKm: record.distanceKm ?? "",
          inTime: record.inTime || "",
          outTime: record.outTime || "",
          totalHours: record.totalHours ?? "",
          expenditureInr: record.expenditureInr ?? "",
          remarks: record.remarks || "",
          supportingDocuments: record.supportingDocuments || "",
          period: record.period || "",
          status: record.status ?? 1,
          createBy: record.createBy || userID,
        });
      } else {
        // No record → POST, keep prefill
        setExistingId(null);
      }
    } catch (error) {
      console.error("Error fetching timesheet:", error);
      setExistingId(null);
    } finally {
      setLoading(false);
    }
  };

  // ─── Auto-calculate totalHours from inTime / outTime ─────────────────────
  const handleTimeChange = (field, value) => {
    const inT = field === "inTime" ? value : form.inTime;
    const outT = field === "outTime" ? value : form.outTime;
    let totalHours = form.totalHours;

    if (inT && outT) {
      const [ih, im] = inT.split(":").map(Number);
      const [oh, om] = outT.split(":").map(Number);
      const totalMins = oh * 60 + om - (ih * 60 + im);
      totalHours = totalMins > 0 ? (totalMins / 60).toFixed(2) : "";
    }
    setForm((prev) => ({ ...prev, [field]: value, totalHours }));
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // ─── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.clientProject?.trim()) {
      toast.warning("Client Project is required.");
      return;
    }

    setSaving(true);

    const payload = {
      IRId: form.IRId || null,
      clientProject: form.clientProject || null,
      clientPo: form.clientPo || null,
      visitDate: form.visitDate ? moment(form.visitDate).format("YYYY-MM-DD") : null,
      vendorDetails: form.vendorDetails || null,
      travelFrom: form.travelFrom || null,
      travelTo: form.travelTo || null,
      modeOfTravel: form.modeOfTravel || null,
      distanceKm: form.distanceKm || null,
      inTime: form.inTime || null,
      outTime: form.outTime || null,
      totalHours: form.totalHours || null,
      expenditureInr: form.expenditureInr || null,
      remarks: form.remarks || null,
      supportingDocuments: form.supportingDocuments || null,
      period: form.period || null,
      createBy: form.createBy || userID,
      status: 1,
    };

    try {
      if (existingId) {
        // PATCH existing record
        await axiosWithToken.patch(
          BASE_URL + `timeSheet/${existingId}`,
          payload
        );
        toast.success("Timesheet updated successfully!");
      } else {
        // POST new record
        await axiosWithToken.post(BASE_URL + `timeSheet`, payload);
        toast.success("Timesheet saved successfully!");
      }
      onClose();
    } catch (error) {
      console.error("Error saving timesheet:", error);
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#eff6ff",
          borderBottom: "1px solid #dbeafe",
          py: 1.5,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <AccessTimeIcon sx={{ color: "#2563eb" }} />
          <Typography variant="h6" fontWeight={600} color="#1e3a5f">
            Time Sheet
          </Typography>
          {/* POST / PATCH indicator */}
          <Typography
            variant="caption"
            sx={{
              ml: 1,
              backgroundColor: existingId ? "#dcfce7" : "#fef9c3",
              color: existingId ? "#166534" : "#854d0e",
              px: 1.2,
              py: 0.3,
              borderRadius: 1,
              fontWeight: 700,
              fontSize: "0.68rem",
            }}
          >
            {existingId ? "● Edit" : "● New"}
          </Typography>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {rowData?.reportNo && (
            <Typography
              variant="caption"
              sx={{
                backgroundColor: "#dbeafe",
                color: "#1d4ed8",
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontWeight: 600,
                fontSize: "0.7rem",
              }}
            >
              {rowData.reportNo}
            </Typography>
          )}
          {rowData?.jobNumber && (
            <Typography
              variant="caption"
              sx={{
                backgroundColor: "#dcfce7",
                color: "#166534",
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontWeight: 600,
                fontSize: "0.7rem",
              }}
            >
              Job: {rowData.jobNumber}
            </Typography>
          )}
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </DialogTitle>

      {/* ── Content ── */}
      <DialogContent sx={{ pt: 5, pb: 1 }}>
        {loading ? (
          <div
            style={{ display: "flex", justifyContent: "center", padding: 40 }}
          >
            <CircularProgress size={28} />
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <TextFields
              label="Client Project *"
              value={form.clientProject}
              onChange={(e) => handleChange("clientProject", e.target.value)}
            />

            {/* <TextFields
              label="Client PO"
              value={form.clientPo}
              onChange={(e) => handleChange("clientPo", e.target.value)}
            /> */}

            <DateInput
              id="Visit-date"
              value={form.visitDate}
              onChange={(date) => {
                const formattedDate = date ? format(date, "yyyy-MM-dd") : "";
                handleChange("visitDate", formattedDate);
              }}
              label={"Visit Date"}
            />

            <TextFields
              label="Vendor Details"
              multiline
              maxRows={2}
              value={form.vendorDetails}
              onChange={(e) => handleChange("vendorDetails", e.target.value)}
            />

            <TextFields
              label="Travel From"
              value={form.travelFrom}
              onChange={(e) => handleChange("travelFrom", e.target.value)}
            />

            <TextFields
              label="Travel To"
              value={form.travelTo}
              onChange={(e) => handleChange("travelTo", e.target.value)}
            />

            <TextFields
              label="Period"
              value={form.period}
              onChange={(e) => handleChange("period", e.target.value)}
            />

            <TextFields
              label="In Time"
              type="time"
              value={form.inTime}
              onChange={(e) => handleTimeChange("inTime", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <TextFields
              label="Out Time"
              type="time"
              value={form.outTime}
              onChange={(e) => handleTimeChange("outTime", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <TextFields
              label="Total Hours"
              type="number"
              value={form.totalHours}
              onChange={(e) => handleChange("totalHours", e.target.value)}
              inputProps={{ step: "0.01", min: 0 }}
            />

            <AutoCompletes
              label="Mode of Travel"
              options={MODES_OF_TRAVEL}
              value={form.modeOfTravel}
              onChange={(val) => handleChange("modeOfTravel", val)}
              size="small"
              fullWidth
              placeholder="Select mode of travel"
            />

            <TextFields
              label="Distance (km)"
              type="number"
              value={form.distanceKm}
              onChange={(e) => handleChange("distanceKm", e.target.value)}
              inputProps={{ min: 0 }}
            />

            <TextFields
              label="Expenditure (INR)"
              type="number"
              value={form.expenditureInr}
              onChange={(e) => handleChange("expenditureInr", e.target.value)}
              inputProps={{ min: 0 }}
            />

            <TextFields
              label="Remarks"
              multiline
              rows={3}
              value={form.remarks}
              onChange={(e) => handleChange("remarks", e.target.value)}
            />
            <FileUploadComponent
              label="Documents"
              fileUploadResponse={form.supportingDocuments}
              setFileUploadResponse={(val) =>
              handleChange("supportingDocuments", val)
              }
            />
          </div>
        )}
      </DialogContent>

      {/* ── Footer Actions ── */}
      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0" }}>
        <Button
          onClick={onClose}
          color="inherit"
          variant="outlined"
          size="small"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          size="small"
          startIcon={
            saving ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
          disabled={saving || loading}
        >
          {saving ? "Saving..." : existingId ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TimeSheetModal;
